import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Prompt } from "react-router";
import { useTranslation } from "react-i18next";
import {
  connect,
  createLocalTracks,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
} from "twilio-video";
import clsx from "clsx";

import { AddonEnum, Message } from "../../../models";
import { useStore } from "../../../stores";

import { ScreenLoader } from "../../../components";

import locale from "../../../constants/locale";
import { logError } from "../../../services/logging";
import { getAttachment } from "../../../utils/messages";

import styles from "./CallContainer.module.scss";
import { DateTime } from "luxon";

const CallContainer = ({ message }: { message: Message }) => {
  const { t } = useTranslation();

  const { addLiveUsage } = useStore("bots");
  const { respondToVideo } = useStore("chats");
  const [room, setRoom] = useState<Room>();
  const [state, setState] = useState("disconnected");
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack>();
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack>();
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const localPreviewRef = useRef<HTMLDivElement>(null);
  const participantPreviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    createLocalTracks()
      .then((tracks) => {
        setVideoTrack(
          tracks.find((f) => f.kind === "video") as LocalVideoTrack
        );
        setAudioTrack(
          tracks.find((f) => f.kind === "audio") as LocalAudioTrack
        );
      })
      .catch(logError);
  }, []);

  const attachment = getAttachment(message);

  useEffect(() => {
    if (!videoTrack || !audioTrack) {
      return;
    }

    let isMounted = true;
    connect(attachment?.payload?.token_agent, {
      name: `${message.id}`,
      tracks: [videoTrack, audioTrack],
    })
      .then((room) => {
        if (isMounted) {
          setRoom(room);
        }
      })
      .catch((e) => {
        logError(e);
      });

    return () => {
      isMounted = false;
    };
  }, [attachment?.payload?.token_agent, audioTrack, message.id, videoTrack]);

  useEffect(() => {
    if (!room) {
      return;
    }

    setState("connected");

    room.on("disconnected", () => setState("disconnected"));
    room.on("reconnecting", () => setState("reconnecting"));
    room.on("reconnected", () => setState("connected"));
    room.on("participantConnected", (participant) => {
      if (participantPreviewRef.current) {
        participantPreviewRef.current.innerHTML = "";
      }

      participant.tracks.forEach((publication) => {
        if (!participantPreviewRef.current) {
          return;
        }

        if (publication.isSubscribed) {
          const track = publication.track;
          if (track) {
            participantPreviewRef.current.appendChild((track as any).attach());
          }
        }
      });

      participant.on("trackSubscribed", (track) => {
        if (!participantPreviewRef.current) {
          return;
        }
        participantPreviewRef.current.appendChild((track as any).attach());
      });
    });

    room.participants.forEach((participant) => {
      participant.tracks.forEach((publication) => {
        if (!participantPreviewRef.current) {
          return;
        }

        if (publication.track) {
          participantPreviewRef.current.appendChild(
            (publication.track as any).attach()
          );
        }
      });

      participant.on("trackSubscribed", (track) => {
        if (!participantPreviewRef.current) {
          return;
        }
        if (track) {
          participantPreviewRef.current.appendChild((track as any).attach());
        }
      });
    });

    if (videoTrack) {
      localPreviewRef.current?.appendChild(videoTrack.attach());
      videoTrack.on("enabled", () => setVideoEnabled(true));
      videoTrack.on("disabled", () => setVideoEnabled(false));
    }

    if (audioTrack) {
      audioTrack.on("enabled", () => setAudioEnabled(true));
      audioTrack.on("disabled", () => setAudioEnabled(false));
    }

    return () => {
      room.disconnect();
      videoTrack?.stop();
      audioTrack?.stop();
    };
  }, [audioTrack, room, videoTrack]);

  const disconnect = async () => {
    const response = await respondToVideo(
      message.id,
      attachment?.payload.status === "sent" ? "canceled" : "completed"
    ) as any;
    room?.disconnect();

    if (response?.attachment?.payload) {
      const {
        status, complete_time, response_time
      } = response.attachment.payload

      if (status === 'completed') {
        const noOfSeconds = DateTime.fromISO(complete_time)
          .diff(DateTime.fromISO(response_time))
          .as('seconds');

        await addLiveUsage(noOfSeconds, AddonEnum.VIDEO_CALL);
      }
    }
  };

  const isConnected = state === "connected";

  return (
    <div className={styles.container}>
      <Prompt when={true} message={t(locale.leavingEndsCall)} />
      <div className={styles.videoPreview}>
        <div ref={participantPreviewRef} className={styles.participantVideo} />
        <div ref={localPreviewRef} className={styles.localVideo} />
      </div>
      <div className={styles.controls}>
        {!isConnected && (
          <div className="is-flex-grow-1">
            <ScreenLoader backgroundColor="rgba(0, 0, 0, 0.4)" />
          </div>
        )}
        <div
          className={clsx(
            "buttons are-large has-addons is-justify-content-center is-align-items-center mb-0",
            styles.toolbar
          )}
        >
          <button
            className="button has-text-white"
            type="button"
            onClick={() =>
              audioEnabled ? audioTrack?.disable() : audioTrack?.enable()
            }
            disabled={!isConnected}
          >
            <span className="icon">
              <i
                className={clsx("fas", {
                  "fa-microphone": audioEnabled,
                  "fa-microphone-slash": !audioEnabled,
                })}
              ></i>
            </span>
          </button>
          <button
            className="button has-text-danger"
            type="button"
            onClick={disconnect}
            disabled={!isConnected}
          >
            <span className="icon">
              <i className="fas fa-phone-slash"></i>
            </span>
          </button>
          <button
            className="button has-text-white"
            type="button"
            onClick={() =>
              videoEnabled ? videoTrack?.disable() : videoTrack?.enable()
            }
            disabled={!isConnected}
          >
            <span className="icon">
              <i
                className={clsx("fas", {
                  "fa-video": videoEnabled,
                  "fa-video-slash": !videoEnabled,
                })}
              ></i>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default observer(CallContainer);
